import { IJobRunDetail, JobTaskStatus } from "@/api/admin/admin.types";
import { Box, Typography } from "@mui/material";
import { CheckCircle2, CircleAlert, Clock, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { formatCompactDuration, isJobTerminal } from "../utils";

interface StatCellProps {
    label: string;
    value: string | number;
    icon: React.ElementType;
    iconColor: string;
    subtitle?: string;
}

const StatCell = ({
    label,
    value,
    icon: Icon,
    iconColor,
    subtitle,
}: StatCellProps) => (
    <Box
        sx={{
            backgroundColor: "#f8fafc",
            borderRadius: "10px",
            px: 2.5,
            py: 2,
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
            <Icon size={14} color={iconColor} />
            <Typography
                variant="caption"
                sx={{
                    color: "#64748b",
                    fontSize: "0.75rem",
                }}
            >
                {label}
            </Typography>
        </Box>
        <Typography
            variant="body1"
            sx={{
                fontWeight: 600,
                fontSize: "1.5rem",
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.2,
            }}
        >
            {value}
        </Typography>
        {subtitle && (
            <Typography
                variant="caption"
                sx={{
                    color: "#64748b",
                    fontSize: "0.75rem",
                    display: "block",
                    mt: 0.5,
                }}
            >
                {subtitle}
            </Typography>
        )}
    </Box>
);

interface JobRunSummaryProps {
    job: IJobRunDetail;
}

export const JobRunSummary = ({ job }: JobRunSummaryProps) => {
    const startTime = job.processing_at;
    const endTime = job.completed_at || job.failed_at;

    const isTerminal = isJobTerminal(job.status);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (isTerminal || !startTime) return;
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [isTerminal, startTime]);

    const totalDuration =
        startTime && endTime
            ? formatCompactDuration(new Date(startTime), new Date(endTime))
            : startTime && !isTerminal
              ? formatCompactDuration(new Date(startTime), new Date(now))
              : "\u2014";

    const startedLabel = startTime
        ? `Started ${format(new Date(startTime), "h:mm a")}`
        : undefined;
    const finishedLabel =
        endTime && isTerminal
            ? `Finished ${format(new Date(endTime), "h:mm a")}`
            : undefined;
    const durationSubtitle = [startedLabel, finishedLabel]
        .filter(Boolean)
        .join(" \u00b7 ");

    const completedCount = job.tasks.filter(
        (t) => t.status === JobTaskStatus.COMPLETED
    ).length;
    const inProgressCount = job.tasks.filter(
        (t) =>
            t.status === JobTaskStatus.PROCESSING ||
            t.status === JobTaskStatus.QUEUED ||
            t.status === JobTaskStatus.RETRYABLE
    ).length;
    const failedCount = job.tasks.filter(
        (t) =>
            t.status === JobTaskStatus.FAILED ||
            t.status === JobTaskStatus.CANCELLED
    ).length;

    return (
        <Box
            sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "1fr 1fr 1fr 1fr",
                },
            }}
        >
            <StatCell
                label="Total Duration"
                value={totalDuration}
                icon={Clock}
                iconColor="#233876"
                subtitle={durationSubtitle || undefined}
            />
            <StatCell
                label="Completed"
                value={completedCount}
                icon={CheckCircle2}
                iconColor="#16a34a"
            />
            <StatCell
                label="In Progress"
                value={inProgressCount}
                icon={Loader2}
                iconColor="#d97706"
            />
            <StatCell
                label="Failed"
                value={failedCount}
                icon={CircleAlert}
                iconColor="#dc2626"
            />
        </Box>
    );
};
